import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import {
  LANGUAGES,
  CURRENCIES,
  formatCurrency,
  convertCurrency,
  getProductPrice,
  getLanguageFromLocale,
  type LanguageConfig,
} from '@/shared/lib/currency-config';

export interface LocalizationContext {
  // Translation
  t: (key: string, options?: any) => string;
  language: string;
  changeLanguage: (lang: string) => void;

  // Currency
  currency: string;
  setCurrency: (curr: string) => void;
  formatPrice: (amount: number, currency?: string) => string;
  convertPrice: (amount: number, fromCurrency: string, toCurrency?: string) => number;

  // Products
  getPrice: (productId: string, period: 'monthly' | 'annual', currency?: string) => number | null;

  // Language info
  currentLanguage: LanguageConfig;
  availableLanguages: LanguageConfig[];
}

export function useLocalization(): LocalizationContext {
  const { t, i18n } = useTranslation();
  const [currency, setCurrencyState] = useState<string>('BRL');

  // Get current language config
  const currentLanguageCode = getLanguageFromLocale(i18n.language);
  const currentLanguage = LANGUAGES[i18n.language] || LANGUAGES['pt-BR'];
  const availableLanguages = Object.values(LANGUAGES);

  // Auto-set currency based on language
  useEffect(() => {
    const defaultCurrency = currentLanguage.defaultCurrency;
    if (defaultCurrency !== currency) {
      setCurrencyState(defaultCurrency);
    }
  }, [currentLanguage.defaultCurrency]);

  // Change language and update currency
  const changeLanguage = async (lang: string) => {
    const fullLocale =
      Object.keys(LANGUAGES).find(key => getLanguageFromLocale(key) === lang) || 'pt-BR';

    await i18n.changeLanguage(fullLocale);

    // Auto-update currency based on new language
    const newLanguageConfig = LANGUAGES[fullLocale];
    if (newLanguageConfig) {
      setCurrencyState(newLanguageConfig.defaultCurrency);
    }
  };

  // Custom currency setter with validation
  const setCurrency = (curr: string) => {
    if (CURRENCIES[curr]) {
      setCurrencyState(curr);
    }
  };

  // Format price with current or specified currency
  const formatPrice = (amount: number, currencyCode?: string): string => {
    const targetCurrency = currencyCode || currency;
    return formatCurrency(amount, targetCurrency, i18n.language);
  };

  // Convert price between currencies
  const convertPrice = (amount: number, fromCurrency: string, toCurrency?: string): number => {
    const targetCurrency = toCurrency || currency;
    return convertCurrency(amount, fromCurrency, targetCurrency);
  };

  // Get product price in current or specified currency
  const getPrice = (
    productId: string,
    period: 'monthly' | 'annual',
    currencyCode?: string
  ): number | null => {
    const targetCurrency = currencyCode || currency;
    return getProductPrice(productId, period, targetCurrency);
  };

  return {
    // Translation
    t,
    language: currentLanguageCode,
    changeLanguage,

    // Currency
    currency,
    setCurrency,
    formatPrice,
    convertPrice,

    // Products
    getPrice,

    // Language info
    currentLanguage,
    availableLanguages,
  };
}

// Helper hook for product pricing
export function useProductPricing(productId: string) {
  const { getPrice, formatPrice, currency, t } = useLocalization();

  const monthlyPrice = getPrice(productId, 'monthly');
  const annualPrice = getPrice(productId, 'annual');

  const formattedMonthly = monthlyPrice ? formatPrice(monthlyPrice) : null;
  const formattedAnnual = annualPrice ? formatPrice(annualPrice) : null;

  // Calculate discount percentage
  const discountPercentage =
    monthlyPrice && annualPrice ? Math.round((1 - annualPrice / 12 / monthlyPrice) * 100) : 15; // Default 15% as per documentation

  return {
    monthlyPrice,
    annualPrice,
    formattedMonthly,
    formattedAnnual,
    discountPercentage,
    currency,
    savings: monthlyPrice && annualPrice ? formatPrice(monthlyPrice * 12 - annualPrice) : null,
    labels: {
      monthly: t('products.monthly'),
      annual: t('products.annual'),
      discount: t('products.annualDiscount'),
    },
  };
}

// Helper hook for language switcher
export function useLanguageSwitcher() {
  const { language, changeLanguage, availableLanguages, currentLanguage } = useLocalization();

  const switcherOptions = availableLanguages.map(lang => ({
    code: getLanguageFromLocale(lang.code),
    name: lang.name,
    nativeName: lang.nativeName,
    active: getLanguageFromLocale(lang.code) === language,
  }));

  return {
    currentLanguage: language,
    currentLanguageNative: currentLanguage.nativeName,
    options: switcherOptions,
    changeLanguage,
  };
}
